import { ConfigurationSourceDataMap, ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Metadata, Note, Participant, SourceData, SourceDataEventStatus, SourceDataFieldType, SourceDataFormStatus, SourceDataReview, SourceDataVerification, Trial } from "@/api/dtos";
import { useMemo } from "react";
import { createItemConfiguration, fieldTypesWithValues } from "../configuration/useItemConfiguration";
import { AppSettings } from "@/AppSettings";
import { createItemValueConfiguration } from "../configuration/useItemValueConfiguration";
import { DateTime } from "luxon";
import { createItemValueConfigurations } from "../configuration/useItemValueConfigurations";

export const createItem = (
    metadata?: Metadata, 
    trialConfigurationItems?: ConfigurationSourceDataMap['items'], 
    configurationItems?: ConfigurationSourceDataMap['items'], 
    sourceDataItems?: SourceData['items'], 
    trialConfigurationItemValues?: ConfigurationSourceDataMap['itemValues'], 
    configurationItemValues?: ConfigurationSourceDataMap['itemValues'],
    trial?: Trial, 
    masterGroup?: MasterGroup,
    coordinatingGroup?: CoordinatingGroup,
    institution?: Institution,
    participant?: Participant,
    isGlobal?: boolean,
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    studyEventRepeat?: number,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string,
    itemGroupRepeat?: number,
    itemName?: string,
    preview?: boolean
) => {
    if (!studyEventRepeat || !itemGroupRepeat) {
        return undefined;
    }

    const itemConfiguration = createItemConfiguration(
        metadata,
        trialConfigurationItems,
        configurationItems,
        trial,
        isGlobal,
        configurationSourceDataId,
        studyEventOid,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemName
    )

    if (!itemConfiguration) {
        return undefined;
    }

    const sourceDataItem = sourceDataItems?.[studyEventOid!]?.[studyEventRepeat]?.[caseReportFormOid!]?.[itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[itemGroupRepeat]?.[itemName!];

    if (!itemConfiguration.configuration?.enabled || itemConfiguration.itemMetadata?.fieldType === SourceDataFieldType.Hidden) {
        return undefined;
    }

    let value = sourceDataItem?.value;

    const fieldType = itemConfiguration.itemMetadata?.fieldType ?? SourceDataFieldType.Text;

    let itemValueConfiguration: ReturnType<typeof createItemValueConfiguration> = undefined;
    let itemValueConfigurations: ReturnType<typeof createItemValueConfigurations> = undefined;

    switch (fieldType) {
        case SourceDataFieldType.Radio:
        case SourceDataFieldType.Select:
            itemValueConfiguration = createItemValueConfiguration(
                metadata,
                trialConfigurationItemValues,
                configurationItemValues,
                trial,
                isGlobal,
                configurationSourceDataId,
                studyEventOid,
                caseReportFormOid,
                caseReportFormVersionOid,
                itemGroupName,
                itemName,
                sourceDataItem?.value
            );

            itemValueConfigurations = createItemValueConfigurations(
                metadata,
                trialConfigurationItemValues,
                configurationItemValues,
                trial,
                isGlobal,
                configurationSourceDataId,
                studyEventOid,
                caseReportFormOid,
                caseReportFormVersionOid,
                itemGroupName,
                itemName
            )

            value = itemValueConfiguration?.displayName ?? sourceDataItem?.value

            break;
        case SourceDataFieldType.MultiSelect:

            itemValueConfigurations = createItemValueConfigurations(
                metadata,
                trialConfigurationItemValues,
                configurationItemValues,
                trial,
                isGlobal,
                configurationSourceDataId,
                studyEventOid,
                caseReportFormOid,
                caseReportFormVersionOid,
                itemGroupName,
                itemName
            )

            const values = sourceDataItem?.value?.split(',');

            value = values?.reduce<string | undefined>((v, id) => {
                const valueDisplayName = itemValueConfigurations?.find(x => x.itemValue === id.trim())?.displayName;
                
                if (v === undefined) {
                    v = '';
                }
                else {
                    v += ', ';
                }

                v += valueDisplayName ?? id;

                return v;
            }, undefined)

        case SourceDataFieldType.Checkbox:
            itemValueConfigurations = createItemValueConfigurations(
                metadata,
                trialConfigurationItemValues,
                configurationItemValues,
                trial,
                isGlobal,
                configurationSourceDataId,
                studyEventOid,
                caseReportFormOid,
                caseReportFormVersionOid,
                itemGroupName,
                itemName
            )


            value = sourceDataItem?.value === "1" ? "Yes" : "No";

            break;
        case SourceDataFieldType.Date:
            value = sourceDataItem?.value ? DateTime.fromISO(sourceDataItem.value).toFormat('dd/MM/yyyy') : undefined;

            break;
        case SourceDataFieldType.Ctcae:
            // const itemGroupItems = sourceDataItems?.[studyEventOid!]?.[studyEventRepeat]?.[caseReportFormOid!]?.[itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[itemGroupRepeat];
            
            let targetTermId = itemConfiguration.itemMetadata?.metadata?.[AppSettings.ctcae.termId];
            // let termId = undefined;
            
            // const termItemName = itemConfiguration.itemMetadata?.metadata?.[AppSettings.ctcae.termItemName];

            // if (termItemName) {
            //     termId = itemGroupItems?.[termItemName]?.value;
            // }

            // let gradeId = sourceDataItem?.value;

            // let specification: string | undefined = undefined;

            // let specificationItemName = itemConfiguration.itemMetadata?.metadata?.[AppSettings.ctcae.specificationItemName];

            // if (specificationItemName) {
            //     specification = itemGroupItems?.[specificationItemName]?.value;
            // }

            // value = JSON.stringify({
            //     termId: targetTermId ? +targetTermId : termId ? +termId : undefined,
            //     gradeId: gradeId ? +gradeId : undefined,
            //     specification: specification,

            //     staticTerm: targetTermId ? true : false
            // });

            value = sourceDataItem?.value ?? JSON.stringify({
                termId: targetTermId,
                staticTerm: targetTermId ? true : false
            })

            break;
        case SourceDataFieldType.File:
            
            const forwardSlashIndex = sourceDataItem?.value?.lastIndexOf('/') ?? -1;
            const backSlashIndex = sourceDataItem?.value?.lastIndexOf('\\') ?? -1;

            if (forwardSlashIndex !== -1 && backSlashIndex !== -1) {
                const index = forwardSlashIndex < backSlashIndex ? backSlashIndex : forwardSlashIndex;
    
                value = sourceDataItem?.value?.substring(index + 1);
            }
            else if (forwardSlashIndex !== -1) {
                value = sourceDataItem?.value?.substring(forwardSlashIndex + 1);
            }
            else if (backSlashIndex !== -1) {
                value = sourceDataItem?.value?.substring(backSlashIndex + 1);
            }
            else {
                value = sourceDataItem?.value;
            }

            break;
        case SourceDataFieldType.MultilineText:
        case SourceDataFieldType.Numeric:
        case SourceDataFieldType.Text:
        default:
            value = sourceDataItem?.value;
            break;
    }

    let link = `/events/${studyEventOid}/${studyEventRepeat}/forms/${caseReportFormOid}`

    if (itemGroupName) {
        link = `${link}/${itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey}/${itemGroupRepeat}/${itemName}`;
    }
    else {
        link = `${link}/${itemName}`;
    }

    return {
        ...itemConfiguration,
        trial,
        masterGroup, 
        coordinatingGroup,
        institution,
        participant,
        preview,
        id: caseReportFormOid as any as number,
        status: ConfigurationStatus.Enabled,
        studyEventRepeat: studyEventRepeat,
        itemGroupRepeat: itemGroupRepeat,
        sourceDataItem: sourceDataItem,
        value: value,
        itemValueConfiguration,
        itemValueConfigurations,

        previewNoteLink: `/${trial?.oid}/preview/participants/${participant?.studyNumber}${link}/notes-and-queries`,
        dataNoteLink: `/${trial?.oid}${institution ? `/institutions/${institution.code}` : ''}/participants/${participant?.studyNumber}${link}/notes-and-queries`,
        previewFindingLink: `/${trial?.oid}/preview/participants/${participant?.studyNumber}${link}/findings`,
        dataFindingLink: `/${trial?.oid}${institution ? `/institutions/${institution.code}` : ''}/participants/${participant?.studyNumber}${link}/findings`,
        previewAuditLink: `/${trial?.oid}/preview/participants/${participant?.studyNumber}${link}/audit`,
        dataAuditLink: `/${trial?.oid}${institution ? `/institutions/${institution.code}` : ''}/participants/${participant?.studyNumber}${link}/audit/data`
    };
}

const useItem = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    sourceData?: SourceData,
    trial?: Trial, 
    masterGroup?: MasterGroup,
    coordinatingGroup?: CoordinatingGroup,
    institution?: Institution,
    participant?: Participant,
    studyEventOid?: string,
    studyEventRepeat?: number,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string,
    itemGroupRepeat?: number,
    itemName?: string,
    preview?: boolean
) => {
    return useMemo(() => {
        const item = createItem(
            metadata, 
            trialConfiguration?.items, 
            configuration?.items,
            sourceData?.items, 
            trialConfiguration?.itemValues, 
            configuration?.itemValues,
            trial, 
            masterGroup,
            coordinatingGroup,
            institution,
            participant,
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id, 
            studyEventOid,
            studyEventRepeat,
            caseReportFormOid,
            caseReportFormVersionOid,
            itemGroupName,
            itemGroupRepeat,
            itemName,
            preview
        );

        return item;
    }, [
        metadata, 
        trialConfiguration?.items, 
        configuration?.items, 
        sourceData?.items, 
        trialConfiguration?.itemValues, 
        configuration?.itemValues,
        trial,
        masterGroup, 
        coordinatingGroup,
        institution, 
        participant, 
        studyEventOid, 
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName,
        preview
    ]);
};

export default useItem;